import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError, LoginResponse } from '../types/auth.types';
import { clearTokens, getAccessToken, getRefreshToken, setAccessToken, setRefreshToken } from './tokenStore';

const USER_STORAGE_KEY = 'sclab_user';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function redirectToLogin(): void {
  clearTokens();
  localStorage.removeItem(USER_STORAGE_KEY);
  window.location.href = '/login';
}

let isRefreshing = false;

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    const codigo = error.response?.data?.codigo;

    if (status === 403 && codigo === 'PASSWORD_EXPIRED') {
      window.location.href = '/change-password?expired=true';
      return Promise.reject(error);
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        redirectToLogin();
        return Promise.reject(error);
      }

      const currentRefreshToken = getRefreshToken();
      if (!currentRefreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      isRefreshing = true;
      try {
        // Cliente sin interceptores para evitar un loop infinito de refresh.
        const plainClient = axios.create({ baseURL: process.env.REACT_APP_API_URL });
        const { data } = await plainClient.post<LoginResponse>('/api/auth/refresh', {
          refreshToken: currentRefreshToken,
        });

        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
